<template>
  <section
    class="lighting-advisor-tool content-section"
    data-testid="lighting-advisor-tool"
  >
    <div class="content-section__inner">
      <Stepper
        :value="String(step)"
        orientation="vertical"
        class="lighting-advisor-tool__stepper finder-stepper"
        data-testid="lighting-advisor-stepper"
        @update:value="onStepperChange"
      >
        <StepItem
          v-for="stepDefinition in stepDefinitions"
          :key="stepDefinition.id"
          :value="stepDefinition.id"
        >
          <Step
            class="finder-stepper__step"
            :class="`finder-stepper__step--${stepDefinition.status}`"
            :style="{ '--finder-step-number': `'${stepDefinition.number}'` }"
          >
            {{ stepDefinition.label }}
          </Step>
          <StepPanel>
            <div
              class="lighting-advisor-tool__panel"
              :data-testid="`lighting-advisor-step-${stepDefinition.number}`"
            >
              <div class="lighting-advisor-tool__step-introduction">
                <h3 class="lighting-advisor-tool__step-title">
                  {{ stepTitle(stepDefinition.number) }}
                </h3>
                <LightingAdvisorStepExplanation
                  :step-number="stepDefinition.number"
                />
              </div>

              <LightingAdvisorQuestionFields
                :layout="questionLayoutForStep(stepDefinition.number)"
                :questions="questionsForStep(stepDefinition.number)"
                :answers="answers"
                :is-question-visible="isQuestionVisible"
                :get-tooltip-keys="getTooltipKeys"
                :get-warning-notes="getWarningNotes"
                :option-images="optionImages"
                :overlay-images="overlayImages"
                @update:answer="setAnswer"
                @update:focused-field="focusedSetupField = $event"
                @tooltip-opened="trackQuestionInformation"
              />

              <LightingAdvisorExampleObjects
                v-if="[1, 2, 3].includes(stepDefinition.number)"
                :configuration="configuration"
                :answers="answers"
                :step="stepDefinition.number"
                :focused-field="focusedSetupField"
                @toggle="trackExamplesToggle(stepDefinition.number, $event)"
              />

              <div
                v-if="
                  stepDefinition.number === 5 && answers.a2s5q2 === 'basler'
                "
                class="lighting-advisor-tool__nested"
                data-testid="lighting-advisor-step-5-basler"
              >
                <div class="lighting-advisor-tool__field">
                  <label
                    class="lighting-advisor-tool__label"
                    for="lighting-advisor-a2s5q2-series"
                  >
                    {{
                      $textDictionary[
                        'lightingAdvisor.question.a2s5q2.series'
                      ] ?? 'Camera series'
                    }}
                  </label>
                  <PrimeVueSelect
                    input-id="lighting-advisor-a2s5q2-series"
                    :model-value="answers.a2s5q2_basler_series ?? undefined"
                    :options="cameraSeriesOptions"
                    option-label="label"
                    option-value="value"
                    data-key="value"
                    :placeholder="
                      $textDictionary['lightingAdvisor.common.pleaseSelect'] ??
                      'Please select'
                    "
                    filter
                    auto-filter-focus
                    reset-filter-on-hide
                    fluid
                    scroll-height="14rem"
                    data-testid="lighting-advisor-input-a2s5q2-series"
                    @update:model-value="
                      setAnswer(
                        'a2s5q2_basler_series',
                        $event ? String($event) : null
                      )
                    "
                  />
                </div>
                <div class="lighting-advisor-tool__field">
                  <label
                    class="lighting-advisor-tool__label"
                    for="lighting-advisor-a2s5q2-camera"
                  >
                    {{
                      $textDictionary[
                        'lightingAdvisor.question.a2s5q2.model'
                      ] ?? 'Camera model'
                    }}
                  </label>
                  <PrimeVueSelect
                    input-id="lighting-advisor-a2s5q2-camera"
                    :model-value="answers.a2s5q2_basler_camera ?? undefined"
                    :options="cameraModelOptions"
                    option-label="label"
                    option-value="value"
                    data-key="value"
                    :placeholder="
                      $textDictionary['lightingAdvisor.common.pleaseSelect'] ??
                      'Please select'
                    "
                    filter
                    auto-filter-focus
                    reset-filter-on-hide
                    fluid
                    scroll-height="14rem"
                    data-testid="lighting-advisor-input-a2s5q2-camera"
                    @update:model-value="
                      setAnswer(
                        'a2s5q2_basler_camera',
                        $event ? String($event) : null
                      )
                    "
                  />
                </div>
              </div>

              <div
                v-if="
                  stepDefinition.number === 5 && answers.a2s5q2 === 'thirdparty'
                "
                class="lighting-advisor-tool__nested"
                data-testid="lighting-advisor-step-5-thirdparty"
              >
                <div class="lighting-advisor-tool__field">
                  <label
                    class="lighting-advisor-tool__label"
                    for="lighting-advisor-a2s5q2-mount"
                  >
                    {{
                      $textDictionary[
                        'lightingAdvisor.question.a2s5q2.mount'
                      ] ?? 'Mount'
                    }}
                  </label>
                  <PrimeVueSelect
                    input-id="lighting-advisor-a2s5q2-mount"
                    :model-value="answers.a2s5q2_thirdparty_mount ?? undefined"
                    :options="mountOptions"
                    option-label="label"
                    option-value="value"
                    data-key="value"
                    :placeholder="
                      $textDictionary['lightingAdvisor.common.pleaseSelect'] ??
                      'Please select'
                    "
                    fluid
                    scroll-height="14rem"
                    data-testid="lighting-advisor-input-a2s5q2-mount"
                    @update:model-value="
                      setAnswer(
                        'a2s5q2_thirdparty_mount',
                        $event ? String($event) : null
                      )
                    "
                  />
                </div>
                <div class="lighting-advisor-tool__field">
                  <label
                    class="lighting-advisor-tool__label"
                    for="lighting-advisor-a2s5q2-monocolor"
                  >
                    {{
                      $textDictionary[
                        'lightingAdvisor.question.a2s5q2.monocolor'
                      ] ?? 'Sensor type'
                    }}
                  </label>
                  <PrimeVueSelect
                    input-id="lighting-advisor-a2s5q2-monocolor"
                    :model-value="
                      answers.a2s5q2_thirdparty_monocolor ?? undefined
                    "
                    :options="monocolorOptions"
                    option-label="label"
                    option-value="value"
                    data-key="value"
                    :placeholder="
                      $textDictionary['lightingAdvisor.common.pleaseSelect'] ??
                      'Please select'
                    "
                    fluid
                    scroll-height="14rem"
                    data-testid="lighting-advisor-input-a2s5q2-monocolor"
                    @update:model-value="
                      setAnswer(
                        'a2s5q2_thirdparty_monocolor',
                        $event ? String($event) : null
                      )
                    "
                  />
                </div>
              </div>

              <div class="lighting-advisor-tool__nav">
                <div class="lighting-advisor-tool__nav-back">
                  <LinkElement
                    v-if="stepDefinition.number > 1"
                    link="javascript:;"
                    style-type="default"
                    icon-type="icon-left"
                    icon-name="arrow-left"
                    data-testid="lighting-advisor-step-back"
                    @click.prevent="goToStep(stepDefinition.number - 1)"
                  >
                    {{ $textDictionary['lightingAdvisor.step.back'] ?? 'Back' }}
                  </LinkElement>
                </div>
                <Button
                  v-if="stepDefinition.number < 5"
                  type="button"
                  style-type="primary"
                  icon-type="icon-right"
                  icon-name="arrow-right"
                  size="small"
                  data-testid="lighting-advisor-step-next"
                  @click="goToStep(stepDefinition.number + 1)"
                >
                  {{ $textDictionary['lightingAdvisor.step.next'] ?? 'Next' }}
                </Button>
              </div>
            </div>
          </StepPanel>
        </StepItem>
      </Stepper>

      <div v-if="resultsTotal > 0" class="lighting-advisor-tool__jump">
        <span class="lighting-advisor-tool__jump-surface">
          <Button
            type="button"
            style-type="secondary"
            icon-type="icon-right"
            icon-name="chevron-down"
            data-testid="lighting-advisor-jump-to-results"
            @click="onJumpToResultsClick"
          >
            {{ jumpToResultsLabel }}
          </Button>
        </span>
      </div>
    </div>

    <LightingAdvisorResultsList
      :light-skus="resultSkus"
      :score-by-sku="scoreBySku"
      :decorations="decorations"
      :answer-summary="answerSummary"
      :loading="resultsLoading"
      :total="resultsTotal"
      :current-page="resultsPage"
      :total-pages="resultsTotalPages"
      @page-change="onResultsPageChange"
      @select="openAccessoryModal"
    />

    <LightingAdvisorAccessoryModal
      v-model="accessoryModalOpen"
      :light-sku="selectedLightSku"
      :answers="answers"
      :decorations="decorations"
      :accessory-configuration="configuration.accessories"
    />
  </section>
</template>

<script setup lang="ts">
import { Button, LinkElement } from '@hypercodestudio/basler-components';
import Stepper from 'primevue/stepper';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import type { LocationQueryRaw } from 'vue-router';
import type { ITechnicalComponent } from '~~/lib/ContentfulService';
import LightingAdvisorQuestionFields from '~/components/global/lib/LightingAdvisor/LightingAdvisorQuestionFields.vue';
import LightingAdvisorStepExplanation from '~/components/global/lib/LightingAdvisor/LightingAdvisorStepExplanation.vue';
import LightingAdvisorExampleObjects from '~/components/global/lib/LightingAdvisor/LightingAdvisorExampleObjects.vue';
import LightingAdvisorResultsList from '~/components/global/lib/LightingAdvisor/LightingAdvisorResultsList.vue';
import LightingAdvisorAccessoryModal from '~/components/global/lib/LightingAdvisor/LightingAdvisorAccessoryModal.vue';
import type {
  LightingAdvisorQuestion,
  LightingAdvisorSetupField,
  LightingAnswers
} from '~/types/lightingAdvisor';
import type { LightingAdvisorDecoration } from '~/utils/lightingAdvisor/catalogTypes';
import { DEFAULT_LIGHT_FUNCTIONS } from '~/utils/lightingAdvisor/catalogTypes';
import {
  buildLightingAdvisorQuestions,
  dictText
} from '~/utils/lightingAdvisor/questionsCatalog';
import {
  cascadeResetAnswers,
  getAllowedLightTypes,
  getQuestionTooltipKeys,
  getWarningNotesForQuestion,
  isQuestionVisible as isQuestionVisibleByAnswers,
  normalizeAnswersAfterLoad
} from '~/utils/lightingAdvisor/questionDependencies';
import {
  buildLightingAdvisorQuery,
  buildLightingAdvisorRouteQuery,
  readLightingAdvisorUrlState
} from '~/utils/lightingAdvisor/lightingAdvisorHash';
import { buildLightingSelectionSummaryItems } from '~/utils/lightingAdvisor/buildLightingSelectionSummary';
import { buildFinderModalSteps } from '~/composables/useFinderModalStepper';
import { useLightingAdvisorAnalytics } from '~/composables/useLightingAdvisorAnalytics';
import { parseLightingAdvisorConfiguration } from '~/utils/lightingAdvisor/configurationSchema';
import { useLightingAdvisorImages } from '~/composables/useLightingAdvisorImages';
import {
  partitionLightingAdvisorDecorations,
  resolveLightingAdvisorWarningMessage
} from '~/utils/lightingAdvisor/warningMessages';

interface Props {
  item: ITechnicalComponent;
}

const props = defineProps<Props>();

const RESULTS_PAGE_SIZE = 24;
const FETCH_DEBOUNCE_MS = 750;
const STEP_NUMBERS = [1, 2, 3, 4, 5];
/** Steps whose questions are laid out side by side, mirroring the legacy tool. */
const COLUMN_LAYOUT_STEPS = [2, 3, 4];

const { $textDictionary, $activeLocale, $shopStore } = useNuxtApp();
const route = useRoute();
const router = useRouter();
const logger = useLogger();
const analytics = useLightingAdvisorAnalytics();

const step = ref(1);
const answers = ref<LightingAnswers>({});
const resultsLoading = ref(true);
const resultsPage = ref(1);
const resultsTotal = ref(0);
const resultsTotalPages = ref(1);
const resultSkus = ref<string[]>([]);
const scoreBySku = ref<Record<string, number>>({});
const decorations = ref<LightingAdvisorDecoration[]>([]);
const accessoryModalOpen = ref(false);
const selectedLightSku = ref<string | null>(null);
const urlStateInitialized = ref(false);

type CameraOption = {
  sku: string;
  series: string | null;
  model_name: string | null;
};

const cameras = ref<CameraOption[]>([]);
const mounts = ref<Array<{ id: string; name: string }>>([]);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let resultsAbortController: AbortController | null = null;

const dictionary = computed(
  () => ($textDictionary.value ?? {}) as Record<string, string | undefined>
);

const configuration = computed(() =>
  parseLightingAdvisorConfiguration(props.item.fields.configuration)
);
const { optionImages, overlayImages } = useLightingAdvisorImages(configuration);
const focusedSetupField = ref<LightingAdvisorSetupField | null>(null);

const questions = computed(() =>
  buildLightingAdvisorQuestions(dictionary.value)
);

const answerSummary = computed(() =>
  buildLightingSelectionSummaryItems({
    answers: answers.value,
    questions: questions.value,
    labels: {
      workingDistance: dictText(
        dictionary.value,
        'lightingAdvisor.question.a2s2q1.content'
      ),
      objectSize: dictText(
        dictionary.value,
        'lightingAdvisor.question.a2s2q2.content'
      ),
      peakWavelength: dictText(
        dictionary.value,
        'lightingAdvisor.question.a2s3q3.content'
      )
    }
  })
);

function stepTitle(stepNumber: number): string {
  return dictText(dictionary.value, `lightingAdvisor.step.${stepNumber}.title`);
}

function stepLabel(stepNumber: number): string {
  return dictText(dictionary.value, `lightingAdvisor.step.${stepNumber}.label`);
}

function trackQuestionInformation(question: LightingAdvisorQuestion): void {
  analytics.trackQuestionInformation(
    stepTitle(Number(question.id.charAt(3))),
    question.label || question.content
  );
}

function trackExamplesToggle(
  stepNumber: number,
  state: 'open' | 'close'
): void {
  analytics.trackEvent('click', `showsample_${stepTitle(stepNumber)}_${state}`);
}

function questionsForStep(stepNumber: number): LightingAdvisorQuestion[] {
  return questions.value.filter((question) =>
    question.id.startsWith(`a2s${stepNumber}`)
  );
}

/**
 * Steps 2–4 ask several short questions, which the legacy tool arranged in two
 * columns. Steps 1 and 5 use full-width choice cards.
 */
function questionLayoutForStep(
  stepNumber: number
): 'wizard-grid' | 'wizard-two-column' {
  return COLUMN_LAYOUT_STEPS.includes(stepNumber)
    ? 'wizard-two-column'
    : 'wizard-grid';
}

/** Object size is split across two inputs and has no answer key of its own. */
function hasAnswerForQuestion(questionId: string): boolean {
  if (questionId === 'a2s2q2') {
    return Boolean(answers.value.a2s2q2_length && answers.value.a2s2q2_width);
  }
  const value = answers.value[questionId];
  return value !== undefined && value !== null && String(value).length > 0;
}

function isStepAnswered(stepNumber: number): boolean {
  const visibleQuestions = questionsForStep(stepNumber).filter((question) =>
    isQuestionVisible(question.id)
  );
  if (!visibleQuestions.length) {
    return false;
  }
  return visibleQuestions.every((question) =>
    hasAnswerForQuestion(question.id)
  );
}

const stepDefinitions = computed(() =>
  buildFinderModalSteps({
    labels: STEP_NUMBERS.map((stepNumber) => stepLabel(stepNumber)),
    ids: STEP_NUMBERS.map((stepNumber) => String(stepNumber)),
    currentIndex: step.value - 1,
    isStepAnswered: (stepId: string) => isStepAnswered(Number(stepId)),
    allowDirectNavigation: true
  })
);

function isQuestionVisible(questionId: string): boolean {
  return isQuestionVisibleByAnswers(questionId, answers.value);
}

function getTooltipKeys(question: LightingAdvisorQuestion): string[] {
  return getQuestionTooltipKeys(question, {
    allowedLightTypes: getAllowedLightTypes(
      answers.value.a2s1q1,
      answers.value.a2s1q2
    ),
    a2s3q1: answers.value.a2s3q1
  });
}

/**
 * Question-scoped warnings the scoring reports for the current answers, e.g.
 * ambient light interference on `a2s2q4`.
 */
const decorationWarningsByQuestion = computed(
  () =>
    partitionLightingAdvisorDecorations(decorations.value).warningsByQuestion
);

function getWarningNotes(question: LightingAdvisorQuestion) {
  const dictionaryNotes = getWarningNotesForQuestion(question, answers.value, {
    allowedLightTypes: getAllowedLightTypes(
      answers.value.a2s1q1,
      answers.value.a2s1q2
    ),
    a2s3q1: answers.value.a2s3q1
  });
  const decorationNotes = (
    decorationWarningsByQuestion.value[question.id] ?? []
  ).map((decoration) => ({
    text: resolveLightingAdvisorWarningMessage(
      decoration.identifier,
      dictionary.value
    )
  }));

  return [...dictionaryNotes, ...decorationNotes];
}

function setAnswer(questionId: string, value: string | null): void {
  answers.value = cascadeResetAnswers(
    { ...answers.value, [questionId]: value },
    questionId
  );
  resultsPage.value = 1;
  scheduleFetch();
  void writeUrlState();
}

const jumpToResultsLabel = computed(() => {
  const template =
    dictionary.value['lightingAdvisor.results.jumpButton'] ??
    'To the matching lights';
  return template.replace('{count}', String(resultsTotal.value));
});

function scrollToResults(): void {
  if (!import.meta.client) {
    return;
  }
  requestAnimationFrame(() => {
    document
      .querySelector('.lighting-advisor-results__header')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

function onJumpToResultsClick(): void {
  analytics.trackJumpToResults();
  scrollToResults();
}

function onStepperChange(value: string | number): void {
  const next = Number(value);
  if (Number.isFinite(next)) {
    goToStep(next);
  }
}

function goToStep(next: number): void {
  const bounded = Math.min(5, Math.max(1, next));
  if (bounded >= 4 && !answers.value.a2s4q1) {
    answers.value = {
      ...answers.value,
      a2s4q1: DEFAULT_LIGHT_FUNCTIONS.join(',')
    };
  }
  step.value = bounded;
  analytics.trackStepNavigation(stepTitle(bounded));
  void writeUrlState();
}

function scheduleFetch(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void fetchResults();
  }, FETCH_DEBOUNCE_MS);
}

function onResultsPageChange(page: number): void {
  resultsPage.value = page;
  void fetchResults();
}

function openAccessoryModal(product: { sku: string; name: string }): void {
  selectedLightSku.value = product.sku;
  accessoryModalOpen.value = true;
  analytics.trackProductSelect(product.name, product.sku);
}

async function writeUrlState(): Promise<void> {
  if (!import.meta.client || !urlStateInitialized.value) {
    return;
  }
  const query = buildLightingAdvisorRouteQuery(
    route.query as Record<string, unknown>,
    step.value,
    answers.value
  );
  try {
    await router.replace({ query: query as LocationQueryRaw, hash: '' });
  } catch {
    // ignore navigation duplicates
  }
}

/**
 * Runs without answers too: the API then scores no criteria, so the initial
 * view lists the complete catalog ordered by price.
 */
async function fetchResults(): Promise<void> {
  resultsAbortController?.abort();
  const controller = new AbortController();
  resultsAbortController = controller;
  resultsLoading.value = true;
  try {
    const response = await $fetch<{
      pagination: {
        total: number;
        current_page: number;
        total_pages: number;
      };
      items: Array<{ sku: string; score: number }>;
      decorations: LightingAdvisorDecoration[];
    }>('/api/tools/lighting/lights', {
      signal: controller.signal,
      query: {
        locale: $activeLocale.value.webapp,
        store: $shopStore.value,
        page: resultsPage.value,
        limit: RESULTS_PAGE_SIZE,
        ...buildLightingAdvisorQuery(step.value, answers.value)
      }
    });
    if (controller.signal.aborted) {
      return;
    }
    resultSkus.value = response.items.map((item) => item.sku);
    scoreBySku.value = Object.fromEntries(
      response.items.map((item) => [item.sku, item.score])
    );
    decorations.value = response.decorations ?? [];
    resultsTotal.value = response.pagination.total;
    resultsTotalPages.value = response.pagination.total_pages;
    analytics.trackResultsContext({
      answers: answers.value,
      resultCount: response.pagination.total,
      decorations: response.decorations ?? []
    });
  } catch (error) {
    if (controller.signal.aborted) {
      return;
    }
    logger.error('Lighting advisor lights fetch failed', error);
    resultSkus.value = [];
    scoreBySku.value = {};
    decorations.value = [];
    resultsTotal.value = 0;
    resultsTotalPages.value = 1;
  } finally {
    if (resultsAbortController === controller) {
      resultsAbortController = null;
      resultsLoading.value = false;
    }
  }
}

const cameraSeriesOptions = computed(() => {
  const names = [
    ...new Set(
      cameras.value
        .map((camera) => camera.series)
        .filter((value): value is string => Boolean(value))
    )
  ];
  return names.map((name) => ({ label: name, value: name }));
});

const cameraModelOptions = computed(() => {
  const series = answers.value.a2s5q2_basler_series;
  return cameras.value
    .filter((camera) => !series || camera.series === series)
    .map((camera) => ({
      label: camera.model_name ?? camera.sku,
      value: camera.sku
    }));
});

const mountOptions = computed(() =>
  mounts.value.map((mount) => ({ label: mount.name, value: mount.id }))
);

const monocolorOptions = computed(() => [
  {
    label:
      $textDictionary.value['lightingAdvisor.question.a2s5q2.mono'] ?? 'Mono',
    value: 'mono'
  },
  {
    label:
      $textDictionary.value['lightingAdvisor.question.a2s5q2.color'] ?? 'Color',
    value: 'color'
  }
]);

async function loadCameraOptions(): Promise<void> {
  try {
    const [cameraResponse, mountResponse] = await Promise.all([
      $fetch<{ cameras: CameraOption[] }>('/api/tools/lighting/cameras', {
        query: { locale: $activeLocale.value.webapp, store: $shopStore.value }
      }),
      $fetch<{ items: Array<{ id: string; name: string }> }>(
        '/api/tools/lighting/mounts',
        {
          query: { locale: $activeLocale.value.webapp, store: $shopStore.value }
        }
      )
    ]);
    cameras.value = cameraResponse.cameras ?? [];
    mounts.value = mountResponse.items ?? [];
  } catch (error) {
    logger.error('Lighting advisor camera catalog failed', error);
  }
}

onMounted(async () => {
  const urlState = readLightingAdvisorUrlState({
    query: route.query as Record<string, unknown>,
    hash: import.meta.client ? window.location.hash : ''
  });
  step.value = urlState.step;
  answers.value = normalizeAnswersAfterLoad(urlState.answers);
  if (step.value >= 4 && !answers.value.a2s4q1) {
    answers.value = {
      ...answers.value,
      a2s4q1: DEFAULT_LIGHT_FUNCTIONS.join(',')
    };
  }
  urlStateInitialized.value = true;
  await writeUrlState();
  await Promise.all([loadCameraOptions(), fetchResults()]);
});

onBeforeUnmount(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  resultsAbortController?.abort();
});
</script>

<style scoped>
.lighting-advisor-tool.content-section {
  padding-bottom: 0;
}

/* The tool sits on the page background, not on a card. */
.lighting-advisor-tool__stepper {
  --p-stepper-steppanel-background: transparent;
}

.lighting-advisor-tool__panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
  padding: 0.5rem 0 1.5rem;
}

.lighting-advisor-tool__step-introduction {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
}

.lighting-advisor-tool__step-title {
  margin: 0;
}

.lighting-advisor-tool__nested {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1.5rem;
}

.lighting-advisor-tool__field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.lighting-advisor-tool__label {
  font-weight: 600;
}

@media (max-width: 767px) {
  .lighting-advisor-tool__nested {
    grid-template-columns: 1fr;
  }
}

.lighting-advisor-tool__nav {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.lighting-advisor-tool__nav-back {
  flex: 1 1 auto;
  min-width: 0;
}

.lighting-advisor-tool__jump {
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

/* Opaque surface behind the button so its tinted background stays solid
   while the sticky button floats above the wizard. */
.lighting-advisor-tool__jump-surface {
  display: inline-flex;
  border-radius: 999px;
  background: var(--bslr-background-full);
}
</style>

<style scoped src="~/components/shop/finder/finder-stepper.css"></style>
